import React, { useState } from 'react';
import {
  Typography, Box, Button, useTheme, useMediaQuery, Collapse, IconButton
} from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  DashboardContainer, UserSidebar, ProfileCard,
  ProfileHeader, ProfileAvatar, ProfileContent,
  SocialLink, BookingsContainer
} from './styled';
import { getAvatarUrl } from 'api/utils';
import BookingList from 'components/organisms/bookingList';
import noAvatar from 'assets/images/no-avatar.png';

const UserMainInfo = ({ profile, bookings, navigateBooking, loading, onBookingUpdated }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showMore, setShowMore] = useState(false);

  const UserProfileSection = () => {
    // Check if profile is empty or incomplete
    const isProfileMissing = profile && (!profile?.description || !profile?.alias);

    // Use thumbnail for sidebar, fallback to noAvatar
    const avatarSrc = getAvatarUrl(profile, noAvatar);

    // Function to navigate to profile page
    const goToProfile = () => {
      window.location.href = '/profile';
    };

    return (
      <UserSidebar className={isMobile ? 'mobile' : ''}>
        <ProfileCard>
          <ProfileHeader>
            <ProfileAvatar src={avatarSrc} />

            {/* Edit button for all profiles */}
            <IconButton
              onClick={goToProfile}
              size="small"
              sx={{
                position: 'absolute',
                right: 12,
                top: 12,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
            >
              <EditIcon fontSize="small" color="primary" />
            </IconButton>
          </ProfileHeader>

          <ProfileContent>
            {/* Always show name if available */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: isMobile ? 2 : 3
            }}>
              <Box>
                <Typography variant="h6" gutterBottom={!isMobile}>
                  {profile?.first_name || ''} {profile?.last_name || ''}
                </Typography>

                {profile?.alias ? (
                  <Typography
                    variant="body2"
                    color="primary.main"
                    sx={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    }}
                  >
                    {profile?.alias}
                  </Typography>
                ) : null}
              </Box>
              {!isProfileMissing && isMobile && (profile?.description || profile?.link_1 || profile?.link_2) && (
                <IconButton
                  onClick={() => setShowMore(!showMore)}
                  size="small"
                  sx={{
                    transition: 'transform 0.3s ease',
                    transform: showMore ? 'rotate(180deg)' : 'none'
                  }}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              )}
            </Box>

            {/* Show completion prompt if profile is missing or incomplete */}
            {(isProfileMissing) && (
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {isProfileMissing
                    ? 'Complete your profile to enhance your workspace experience'
                    : 'Add more details to your profile'}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={goToProfile}
                  size="small"
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none'
                  }}
                >
                  {isProfileMissing ? 'Complete Profile' : 'Update Profile'}
                </Button>
              </Box>
            )}

            {/* Only show additional info if profile exists */}
            {!isProfileMissing && (
              <>
                {/* Additional Info Section */}
                {isMobile ? (
                  <Collapse in={showMore}>
                    {/* Description */}
                    {profile?.description && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.6,
                            borderTop: 1,
                            borderColor: 'divider',
                            pt: 2
                          }}
                        >
                          {profile?.description}
                        </Typography>
                      </Box>
                    )}

                    {/* Social Links */}
                    {(profile?.link_1 || profile?.link_2) && (
                      <Box sx={{
                        borderTop: profile?.description ? 0 : 1,
                        borderColor: 'divider',
                        pt: profile?.description ? 0 : 2
                      }}>
                        {profile?.link_1 && (
                          <SocialLink href={profile?.link_1} target="_blank">
                            <LinkedInIcon fontSize="small" />
                            <Typography variant="body2">LinkedIn</Typography>
                          </SocialLink>
                        )}
                        {profile?.link_2 && (
                          <SocialLink href={profile?.link_2} target="_blank">
                            <LanguageIcon fontSize="small" />
                            <Typography variant="body2">Website</Typography>
                          </SocialLink>
                        )}
                      </Box>
                    )}
                  </Collapse>
                ) : (
                  // Desktop view - always expanded
                  <>
                    {profile?.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 3,
                          lineHeight: 1.6,
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {profile?.description}
                      </Typography>
                    )}
                    {(profile?.link_1 || profile?.link_2) && (
                      <Box>
                        {profile?.link_1 && (
                          <SocialLink href={profile?.link_1} target="_blank">
                            <LinkedInIcon fontSize="small" />
                            <Typography variant="body2">LinkedIn</Typography>
                          </SocialLink>
                        )}
                        {profile?.link_2 && (
                          <SocialLink href={profile?.link_2} target="_blank">
                            <LanguageIcon fontSize="small" />
                            <Typography variant="body2">Website</Typography>
                          </SocialLink>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </ProfileContent>
        </ProfileCard>
      </UserSidebar>
    );
  };

  return (
    <DashboardContainer>
      <UserProfileSection />
      <BookingsContainer>


        <BookingList
          bookings={bookings}
          filters={null}
          handleSortChange={null}
          handlePageChange={null}
          navigateBooking={navigateBooking}
          paginationInfo={null}
          expandNextBooking={true}
          onBookingUpdated={onBookingUpdated}
          loadingBookings={loading}
        />

      </BookingsContainer>
    </DashboardContainer>
  );
};

export default UserMainInfo;